import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { I18nProvider } from 'react-aria';
import { Provider } from 'react-aria-components';
import { timeZonesToRemove, validTimezones } from '@gonfalon/datetime/timezones';
import { msg } from '@gonfalon/messages/msg';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { CalendarDate, getLocalTimeZone, now, parseAbsolute, Time } from '@internationalized/date';
import type { DatePickerProps as AriaDatePickerProps, TimeFieldProps } from '@launchpad-ui/components';
import {
  Button,
  ButtonContext,
  Calendar,
  CalendarCell,
  CalendarGrid,
  ComboBox,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  FieldError,
  Group,
  Heading,
  IconButton,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  PopoverContext,
  TimeField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import styles from './DateTimePicker.module.css';

interface TimezoneProps {
  tzid: string;
  onChange?: (tzid: string) => void;
  label?: ReactNode;
  enabled?: boolean;
  className?: string;
}

interface DatePickerProps extends AriaDatePickerProps<CalendarDate> {
  label?: ReactNode;
  className?: string;
  defaultDate?: number;
  minDate?: number;
  maxDate?: number;
}

export interface TimePickerProps extends TimeFieldProps<Time> {
  label?: ReactNode;
  className?: string;
}

export interface DateTimePickerProps {
  className?: string;
  /** Controlled value as epoch milliseconds. When provided, the displayed date/time are
   *  derived from this (in the selected timezone) and the component is controlled — the
   *  parent owns the value and must update it via `onChange`. Pass `null` for a controlled
   *  empty value (cleared). Omit to use uncontrolled mode seeded from
   *  `datepickerProps.defaultDate`. */
  value?: number | null;
  onChange?: (value: number) => void;
  name?: string;
  /** Disables the whole control — date picker, time field, and timezone combobox. */
  isDisabled?: boolean;
  /** Disallow selecting dates/times in the past. Sugar over React Aria's standard `minValue`
   *  (https://react-aria.adobe.com/DatePicker#minvalue): resolves to `minValue = now()`, which
   *  floors the calendar to today and rejects past times. Combined with an explicit
   *  `datepickerProps.minDate`, the later of the two wins. */
  disablePast?: boolean;
  /** How the date portion is presented.
   *  - `field` (default): a segmented date field whose chevron opens the calendar in a popover.
   *    Right for inline form rows, where the calendar should stay out of the way until asked for.
   *  - `calendar`: the calendar is rendered inline, always visible, with the time and timezone
   *    fields stacked beneath it. Right for surfaces that are *already* a popover or modal
   *    dedicated to picking a date — it saves a click and avoids a popover inside a popover. */
  layout?: 'field' | 'calendar';
  timepickerProps?: TimePickerProps;
  datepickerProps?: DatePickerProps;
  timezoneProps: TimezoneProps;
}

function toZonedDate(epochMs: number, tzid?: string): Date {
  if (tzid) {
    return toZonedTime(epochMs, tzid);
  }
  return new Date(epochMs);
}

function toCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function toTimeValue(date: Date): Time {
  return new Time(date.getHours(), date.getMinutes(), 0, 0);
}

function getControlledZonedDate(value: number | null | undefined, tzid: string): Date | undefined {
  if (typeof value !== 'number') {
    return undefined;
  }
  return toZonedDate(value, tzid);
}

function getEffectiveDate(
  isControlled: boolean,
  controlledZoned: Date | undefined,
  selectedDate: CalendarDate | undefined,
): CalendarDate | null | undefined {
  if (!isControlled) {
    return selectedDate;
  }
  if (controlledZoned) {
    return toCalendarDate(controlledZoned);
  }
  return selectedDate ?? null;
}

function getDefaultTime(disablePast: boolean | undefined, timeZone: string | undefined): Time {
  if (!disablePast) {
    return new Time(0, 0, 0, 0);
  }
  const zonedNow = now(timeZone || getLocalTimeZone());
  return new Time(zonedNow.hour, zonedNow.minute, zonedNow.second, zonedNow.millisecond);
}

function getEffectiveTime(
  isControlled: boolean,
  controlledZoned: Date | undefined,
  selectedTime: Time | undefined,
): Time | null | undefined {
  if (!isControlled) {
    return selectedTime;
  }
  if (controlledZoned) {
    return toTimeValue(controlledZoned);
  }
  return selectedTime ?? null;
}

export function DateTimePicker({
  name,
  className,
  value,
  onChange,
  isDisabled,
  disablePast,
  layout = 'field',
  timepickerProps = {},
  datepickerProps = {},
  timezoneProps,
}: DateTimePickerProps) {
  const datePickerButtonRef = useRef<HTMLButtonElement>(null);
  const {
    tzid: defaultTzid,
    label: timezoneLabel,
    enabled: timezoneEnabled = true,
    onChange: onChangeTimezone,
  } = timezoneProps;
  const {
    label: datepickerLabel = 'Date',
    defaultDate,
    minDate,
    maxDate,
    className: datepickerClassName,
    ...restDatepickerProps
  } = datepickerProps;
  const { label: timepickerLabel = 'Time', className: timepickerClassName, ...restTimepickerProps } = timepickerProps;
  const [filterTz, setFilterTz] = useState(defaultTzid);
  const [tzid, setTzid] = useState(defaultTzid);
  const tzidRef = useLatestRef(tzid);
  // Initialize date/time state from props: use toZonedTime when tzid is set so display is in that zone.
  // Controlled-empty (`value === null`) starts blank so `defaultDate` cannot leak into a cleared field.
  const [selectedDate, setSelectedDate] = useState<CalendarDate | undefined>(() => {
    if (value === null || !defaultDate) {
      return undefined;
    }
    return toCalendarDate(toZonedDate(defaultDate, defaultTzid));
  });

  const [selectedTime, setSelectedTime] = useState<Time | undefined>(() => {
    if (value === null || !defaultDate) {
      return undefined;
    }
    return toTimeValue(toZonedDate(defaultDate, defaultTzid));
  });
  // `disablePast` floors the minimum to "now" (React Aria's standard `minValue`); if an explicit
  // minDate is also set, the later wins.
  const effectiveMinDate = disablePast ? Math.max(Date.now(), minDate ?? 0) : minDate;
  const boundsZone = tzid || getLocalTimeZone();
  const minValue = effectiveMinDate ? parseAbsolute(new Date(effectiveMinDate).toISOString(), boundsZone) : undefined;
  const maxValue = maxDate ? parseAbsolute(new Date(maxDate).toISOString(), boundsZone) : undefined;

  // Controlled vs uncontrolled: `undefined` is uncontrolled (internal state / defaultDate).
  // `null` is controlled-empty so a parent can clear the value without the picker falling
  // back to its last internal date. A number is a controlled instant.
  const isControlled = value !== undefined;
  const [previousValue, setPreviousValue] = useState(value);
  // Drop draft date/time when the parent clears a numeric value. Otherwise a time typed
  // after unchecking would sit on top of the previous date, or a clear would flash it back.
  if (value !== previousValue) {
    setPreviousValue(value);
    if (value === null) {
      setSelectedDate(undefined);
      setSelectedTime(undefined);
    }
  }
  const controlledZoned = getControlledZonedDate(value, tzid);
  // Pass `null` (not `undefined`) when controlled-empty so React Aria stays in controlled
  // mode. While empty, keep local drafts so time-first (or date-first) entry can persist
  // until there is enough to emit an onChange timestamp.
  const effectiveDate = getEffectiveDate(isControlled, controlledZoned, selectedDate);
  const effectiveTime = getEffectiveTime(isControlled, controlledZoned, selectedTime);

  const timezoneOptions = validTimezones
    .map((tz) => ({ value: tz, label: tz }))
    .filter((tz) => !timeZonesToRemove.includes(tz.value));

  // Validate that the selected time is not in the past
  const validateTime = (time: Time | undefined) => {
    if (!effectiveDate || !time || !effectiveMinDate) {
      return null;
    }

    const fullDate = new Date(
      effectiveDate.year,
      effectiveDate.month - 1,
      effectiveDate.day,
      time.hour,
      time.minute,
      0,
      0,
    );

    let utcMs: number;

    if (tzid) {
      utcMs = fromZonedTime(fullDate, tzid).getTime();
    } else {
      utcMs = fullDate.getTime();
    }

    if (utcMs < Math.floor(effectiveMinDate / 60_000) * 60_000) {
      return msg('ui.dateTimePicker.mustBeInFutureError');
    }

    return null;
  };

  const onChangeCallback = (params: {
    time?: Time | undefined;
    date?: CalendarDate | undefined;
    timeZone?: string | undefined;
  }) => {
    const { date = effectiveDate, timeZone = tzid } = params;
    const time = params.time ?? effectiveTime ?? getDefaultTime(disablePast, timeZone);
    if (!date) {
      return null;
    }

    const localDate = new Date(
      date.year,
      date.month - 1, // JavaScript months are 0-based
      date.day,
      time.hour,
      time.minute,
      time.second,
      time.millisecond,
    );

    const newDate = timeZone ? fromZonedTime(localDate, timeZone).getTime() : localDate.getTime();

    if (timeZone) {
      onChangeTimezone?.(timeZone);
    }
    onChange?.(newDate);
  };

  const isCalendarLayout = layout === 'calendar';

  // Shared between both layouts: in `field` it lives inside the popover Dialog, in `calendar`
  // it is rendered directly. React Aria's DatePicker supplies CalendarContext either way, so the
  // calendar stays wired to the same value/onChange/minValue/maxValue without a trigger.
  const calendar = (
    <Calendar>
      <header>
        <IconButton
          slot="previous"
          icon="chevron-left"
          aria-label={msg('ui.dateTimePicker.previousMonthLabel')}
          size="small"
          variant="minimal"
        />
        <Heading />
        <IconButton
          slot="next"
          icon="chevron-right"
          aria-label={msg('ui.dateTimePicker.nextMonthLabel')}
          size="small"
          variant="minimal"
        />
      </header>
      <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
    </Calendar>
  );

  return (
    <div className={clsx(styles.dateTimePicker, isCalendarLayout && styles.calendarLayout, className)}>
      {/* Force en-US locale so date/time segments always render MM/DD/YYYY and 12-hour time,
         matching the legacy DayInput/TimePicker behavior. Without this, browsers in 24-hour
         locales show DD.MM.YYYY + HH:MM which causes date-entry bugs in some browsers. */}
      <I18nProvider locale="en-US">
        <DatePicker
          // Spread first so the component-managed props below (value, isDisabled, …) win over
          // anything passed via datepickerProps. isDisabled is OR-merged so a per-field
          // datepickerProps.isDisabled still disables, while a top-level isDisabled always wins.
          {...restDatepickerProps}
          value={effectiveDate}
          minValue={minValue}
          maxValue={maxValue}
          onChange={(nextDate) => {
            setSelectedDate(nextDate ?? undefined);
            onChangeCallback({ date: nextDate ?? undefined });
          }}
          aria-label={restDatepickerProps['aria-label'] ?? 'Select date'}
          className={clsx(styles.datePicker, datepickerClassName)}
          name={`${name}-date`}
          granularity="day"
          hideTimeZone
          isDisabled={isDisabled || restDatepickerProps.isDisabled}
        >
          {datepickerLabel && <Label>{datepickerLabel}</Label>}
          {isCalendarLayout ? (
            calendar
          ) : (
            /* Launchpad's DatePicker skips providing ButtonContext/PopoverContext when inside a Form, so the Popover has no triggerRef and renders at (0,0). Re-provide them here. */
            <Provider
              values={[
                [
                  ButtonContext,
                  {
                    ref: datePickerButtonRef,
                    isDisabled: isDisabled || restDatepickerProps.isDisabled,
                    className: clsx(styles.datePickerButton, restDatepickerProps.isInvalid && styles.invalid),
                  },
                ],
                [PopoverContext, { trigger: 'DatePicker', triggerRef: datePickerButtonRef, placement: 'bottom start' }],
              ]}
            >
              <Button>
                <Icon name="calendar" size="small" />
                <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
                <Icon name="chevron-down" size="small" />
              </Button>
              <Popover>
                <Dialog>{calendar}</Dialog>
              </Popover>
            </Provider>
          )}
        </DatePicker>
        <TimeField
          // Spread first so the component-managed props below win over anything passed via
          // timepickerProps; isDisabled is OR-merged (per-field disables, top-level always wins).
          {...restTimepickerProps}
          aria-label={restTimepickerProps['aria-label'] ?? 'Select time'}
          className={clsx(styles.timeField, timepickerClassName)}
          name={`${name}-time`}
          validate={validateTime}
          // Calendar layout only. React Aria's default `native` behavior never reports validation
          // outside a form submit, so `aria` is what actually surfaces `validate`/`disablePast`
          // messages — but in the `field` layout the row is `align-items: flex-end` + `flex-wrap`,
          // so a message (and the invalid styling that comes with it) grows the time column and
          // knocks the date and timezone controls off the input baseline. Showing it there needs a
          // layout fix first; tracked separately.
          validationBehavior={isCalendarLayout ? 'aria' : undefined}
          value={effectiveTime}
          onChange={(nextTime) => {
            setSelectedTime(nextTime ?? undefined);

            onChangeCallback({ time: nextTime ?? undefined });
          }}
          isDisabled={isDisabled || restTimepickerProps.isDisabled}
        >
          {timepickerLabel && <Label>{timepickerLabel}</Label>}
          <Group>
            <Icon name="clock" size="small" />
            <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
          </Group>
          {isCalendarLayout && <FieldError />}
        </TimeField>
      </I18nProvider>
      {timezoneEnabled && (
        <ComboBox
          aria-label={msg('ui.dateTimePicker.timezoneLabel')}
          isDisabled={isDisabled}
          className={clsx(styles.timeZonePicker, timezoneProps.className)}
          inputValue={filterTz}
          defaultInputValue={tzid}
          onInputChange={(input) => setFilterTz(input)}
          onOpenChange={(open) => {
            if (open) {
              setFilterTz('');
            } else {
              setFilterTz(tzidRef.current);
            }
          }}
          onSelectionChange={(key) => {
            if (key === null || key === undefined) {
              return;
            }
            const next = key as string;
            setTzid(next);
            tzidRef.current = next;
            setFilterTz(next);
            if (isControlled) {
              // Controlled: `value` is an instant — preserve it across a timezone change.
              // The display re-derives from `value` in the new zone on re-render; rebuilding
              // the epoch from the old zone's wall-clock segments would silently shift it.
              onChangeTimezone?.(next);
              return;
            }
            onChangeCallback({ timeZone: next });
          }}
        >
          {timezoneLabel && <Label>{timezoneLabel}</Label>}
          <Group>
            <Input name={`${name}-timezone`} placeholder={msg('ui.dateTimePicker.timezonePlaceholder')} />
            <IconButton
              aria-label={msg('ui.dateTimePicker.showSuggestionsLabel')}
              icon="chevron-down"
              size="small"
              variant="minimal"
            />
          </Group>
          <Popover placement="bottom end" className={styles.popover}>
            <ListBox items={timezoneOptions}>
              {timezoneOptions.map(
                (option) =>
                  option.value.toLocaleLowerCase().includes(filterTz.toLocaleLowerCase()) && (
                    <ListBoxItem textValue={option.label} key={option.value} id={option.value}>
                      {option.label}
                    </ListBoxItem>
                  ),
              )}
            </ListBox>
          </Popover>
        </ComboBox>
      )}
    </div>
  );
}
